(() => {
  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'pl-PL', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'PLN'
      }).format(value);
    } catch (_) {
      return `${value.toFixed(2).replace('.', ',')} zł`;
    }
  };

  const normalizeConfigKey = (value) => String(value || '')
    .trim()
    .replace(/\s*\|\s*/g, ' | ')
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('pl-PL');

  const setMainImage = (root, imageSrc) => {
    const image = root?.querySelector('[data-product-main-image]');
    if (!image || !imageSrc) return;
    image.src = imageSrc;
    image.srcset = '';
  };

  const setupConfigurator = (root) => {
    const form = root.querySelector('form.configurator-form');
    const variantSelect = root.querySelector('[data-configurator-variant-select]');
    const quantityInput = root.querySelector('[data-configurator-quantity]');
    const unitPrice = root.querySelector('[data-configurator-unit-price]');
    const totalPrice = root.querySelector('[data-configurator-total-price]');
    const projectRow = root.querySelector('[data-configurator-project-row]');
    const projectPrice = root.querySelector('[data-configurator-project-price]');
    const status = root.querySelector('[data-configurator-status]');
    const submitButton = root.querySelector('[data-add-to-cart]');
    const quantityHelp = root.querySelector('[data-configurator-quantity-help]');
    const settingsNode = root.parentElement?.querySelector('[data-configurator-settings]') || document.querySelector('[data-configurator-settings]');
    let settings = { engravingMinimum: 10, designService: null, cartAddUrl: '/cart/add.js', cartUrl: '/cart' };

    try {
      if (settingsNode) settings = { ...settings, ...JSON.parse(settingsNode.textContent) };
    } catch (_) {}

    if (!form || !variantSelect || !quantityInput || !submitButton) return;

    const getValue = (propertyName) => form.querySelector(`input[name="properties[${propertyName}]"]:checked`)?.value || '';
    const designSelected = () => getValue('Projekt graficzny') === 'Projekt przygotowujemy my';

    const getKey = () => [
      getValue('Kolor podstawki'),
      getValue('Grawer'),
      getValue('NFC'),
      getValue('Kod QR')
    ].join(' | ');

    const findVariant = () => {
      const key = normalizeConfigKey(getKey());
      return [...variantSelect.options].find((option) => normalizeConfigKey(option.dataset.configKey || option.textContent) === key);
    };

    const setStatus = (message, isError = false) => {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('configurator-status--error', isError);
    };

    const update = ({ preserveQuantity = false } = {}) => {
      const engravingSelected = getValue('Grawer') === (root.dataset.engravingPaidValue || 'Z grawerem');
      const minimum = engravingSelected ? Number(settings.engravingMinimum || 10) : 1;
      quantityInput.min = String(minimum);

      let quantity = Math.max(1, Number.parseInt(quantityInput.value, 10) || 1);
      if (engravingSelected && quantity < minimum && !preserveQuantity) {
        quantity = minimum;
        quantityInput.value = String(minimum);
      }

      if (quantityHelp) {
        quantityHelp.textContent = engravingSelected
          ? `Produkt z grawerem można zamówić od ${minimum} sztuk.`
          : 'Bez graweru możesz zamówić od 1 sztuki.';
      }

      const variant = findVariant();
      if (!variant) {
        if (unitPrice) unitPrice.textContent = '—';
        if (totalPrice) totalPrice.textContent = '—';
        if (projectRow) projectRow.hidden = !designSelected();
        setStatus('Ta konfiguracja nie ma przypisanego wariantu i ceny w Shopify.', true);
        submitButton.disabled = true;
        submitButton.textContent = 'Konfiguracja niedostępna';
        return;
      }

      variantSelect.value = variant.value;
      const price = Number(variant.dataset.price || 0);
      const variantAvailable = variant.dataset.available === 'true';
      const projectIsSelected = designSelected();
      const designService = settings.designService;
      const designConfigured = Boolean(designService?.variantId);
      const designAvailable = !projectIsSelected || (designConfigured && designService.available !== false);
      const designFee = projectIsSelected && designConfigured ? Number(designService.price || 0) : 0;
      const available = variantAvailable && designAvailable;

      if (unitPrice) unitPrice.textContent = formatMoney(price);
      if (totalPrice) totalPrice.textContent = formatMoney((price * quantity) + designFee);
      if (projectRow) projectRow.hidden = !projectIsSelected;
      if (projectPrice) projectPrice.textContent = designConfigured ? formatMoney(designFee) : 'Nie skonfigurowano';
      setMainImage(root, variant.dataset.imageSrc);

      if (projectIsSelected && !designConfigured) {
        setStatus('Usługa przygotowania projektu nie została jeszcze przypisana w ustawieniach sekcji.', true);
      } else if (projectIsSelected && designService.available === false) {
        setStatus('Usługa przygotowania projektu jest obecnie niedostępna.', true);
      } else {
        setStatus(variantAvailable ? 'Wybrana konfiguracja jest dostępna.' : 'Wybrana konfiguracja jest obecnie niedostępna.', !variantAvailable);
      }

      submitButton.disabled = !available;
      submitButton.textContent = available ? submitButton.dataset.availableText : submitButton.dataset.soldOutText;
    };

    form.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"], [data-configurator-quantity]')) update();
    });
    quantityInput.addEventListener('input', () => update({ preserveQuantity: true }));

    form.addEventListener('submit', async (event) => {
      const minimum = Number(quantityInput.min || 1);
      const quantity = Number.parseInt(quantityInput.value, 10) || 0;
      const variant = findVariant();
      const projectIsSelected = designSelected();
      const designService = settings.designService;

      if (quantity < minimum || !variant) {
        event.preventDefault();
        setStatus(
          quantity < minimum
            ? `Minimalna liczba sztuk dla wybranej konfiguracji to ${minimum}.`
            : 'Wybrana konfiguracja nie ma przypisanego wariantu w Shopify.',
          true
        );
        quantityInput.focus();
        return;
      }

      if (!projectIsSelected) return;

      event.preventDefault();
      if (!designService?.variantId || designService.available === false) {
        setStatus('Nie można dodać usługi projektu. Sprawdź jej konfigurację w edytorze motywu.', true);
        return;
      }

      const properties = {};
      new FormData(form).forEach((value, name) => {
        const match = name.match(/^properties\[(.+)]$/);
        if (match && value !== '') properties[match[1]] = value;
      });

      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Dodawanie…';
      setStatus('Dodajemy produkt i jednorazową usługę przygotowania projektu.');

      try {
        const response = await fetch(settings.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number(variant.value),
                quantity,
                properties
              },
              {
                id: Number(designService.variantId),
                quantity: 1,
                properties: {
                  '_Usługa projektu AMtap': 'Tak',
                  'Usługa': 'Przygotowanie projektu graficznego',
                  'Dotyczy produktu': root.dataset.productTitle || ''
                }
              }
            ]
          })
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || error.message || 'Nie udało się dodać konfiguracji do koszyka.');
        }

        window.location.assign(settings.cartUrl || '/cart');
      } catch (error) {
        submitButton.disabled = false;
        submitButton.textContent = originalText;
        setStatus(error.message || 'Nie udało się dodać konfiguracji do koszyka.', true);
      }
    });

    update();
  };

  window.amtapInpostPointSelected = (point) => {
    const input = document.querySelector('[data-parcel-locker-input]');
    if (!input || !point) return;
    const address = [point.address?.line1, point.address?.line2].filter(Boolean).join(', ');
    input.value = [point.name, address].filter(Boolean).join(' — ');
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const map = document.querySelector('[data-parcel-map]');
    const toggle = document.querySelector('[data-parcel-map-toggle]');
    if (map) map.hidden = true;
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = 'Zmień Paczkomat na mapie';
    }
    input.focus();
  };

  const setupCart = (form) => {
    const deliveryRadios = [...form.querySelectorAll('input[name="attributes[Preferowany sposób dostawy]"]')];
    const parcelPanel = form.querySelector('[data-parcel-locker-panel]');
    const parcelInput = form.querySelector('[data-parcel-locker-input]');
    const errorBox = form.querySelector('[data-cart-error]');

    const updateDelivery = () => {
      const value = deliveryRadios.find((radio) => radio.checked)?.value;
      const requiresLocker = value === 'Paczkomat InPost';
      if (parcelPanel) parcelPanel.hidden = !requiresLocker;
      if (parcelInput) parcelInput.required = requiresLocker;
    };

    const showError = (message, field) => {
      if (errorBox) {
        errorBox.textContent = message;
        errorBox.hidden = false;
      }
      field?.focus();
    };

    deliveryRadios.forEach((radio) => radio.addEventListener('change', updateDelivery));
    const mapToggle = form.querySelector('[data-parcel-map-toggle]');
    const parcelMap = form.querySelector('[data-parcel-map]');
    mapToggle?.addEventListener('click', () => {
      const willOpen = parcelMap?.hidden !== false;
      if (parcelMap) parcelMap.hidden = !willOpen;
      mapToggle.setAttribute('aria-expanded', String(willOpen));
      mapToggle.textContent = willOpen ? 'Zamknij mapę' : 'Wybierz Paczkomat z mapy';
    });
    updateDelivery();

    form.addEventListener('submit', (event) => {
      const action = event.submitter?.name || '';
      const engravedInvalid = [...form.querySelectorAll('[data-engraved-quantity]')].find((input) => {
        const quantity = Number.parseInt(input.value, 10) || 0;
        return quantity > 0 && quantity < 10;
      });

      if (engravedInvalid) {
        event.preventDefault();
        showError('Produkt z grawerem musi pozostać w koszyku w ilości co najmniej 10 sztuk albo zostać usunięty przez ustawienie ilości 0.', engravedInvalid);
        return;
      }

      const designRequired = [...form.querySelectorAll('[data-design-required-quantity]')].some((input) => (Number.parseInt(input.value, 10) || 0) > 0);
      const designServicePresent = [...form.querySelectorAll('[data-design-service-quantity]')].some((input) => (Number.parseInt(input.value, 10) || 0) > 0);
      if (designRequired && !designServicePresent) {
        event.preventDefault();
        showError('Wybrano przygotowanie projektu, ale usługa projektu została usunięta z koszyka. Wróć do produktu i dodaj konfigurację ponownie.', form.querySelector('[data-design-required-quantity]'));
        return;
      }
      if (!designRequired && designServicePresent) {
        event.preventDefault();
        showError('W koszyku pozostała usługa projektu bez produktu, którego dotyczy. Ustaw jej ilość na 0 albo dodaj konfigurację ponownie.', form.querySelector('[data-design-service-quantity]'));
        return;
      }

      if (action !== 'checkout') return;

      const selectedDelivery = deliveryRadios.find((radio) => radio.checked);
      if (!selectedDelivery) {
        event.preventDefault();
        showError('Wybierz preferowany sposób dostawy.', deliveryRadios[0]);
        return;
      }

      if (selectedDelivery.value === 'Paczkomat InPost' && !parcelInput?.value.trim()) {
        event.preventDefault();
        showError('Wpisz kod lub nazwę Paczkomatu InPost.', parcelInput);
        return;
      }

      if (errorBox) errorBox.hidden = true;
    });
  };

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-quantity-change]');
    if (quantityButton) {
      const wrapper = quantityButton.closest('[data-quantity]');
      const input = wrapper?.querySelector('input[type="number"]');
      if (!input) return;
      const step = Number(input.step || 1);
      const min = Number(input.min || 1);
      const direction = quantityButton.dataset.quantityChange === 'increase' ? 1 : -1;
      input.value = String(Math.max(min, Number(input.value || min) + (direction * step)));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const thumb = event.target.closest('[data-product-thumbnail]');
    if (thumb) {
      const root = thumb.closest('[data-product-root]') || document;
      setMainImage(root, thumb.dataset.imageSrc);
      root.querySelectorAll('[data-product-thumbnail]').forEach((element) => element.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
    }
  });

  document.addEventListener('change', (event) => {
    const variantSelect = event.target.closest('[data-variant-select]');
    if (variantSelect) {
      const option = variantSelect.selectedOptions[0];
      const root = variantSelect.closest('[data-product-root]');
      const price = root?.querySelector('[data-product-price]');
      const button = root?.querySelector('[data-add-to-cart]');
      if (price && option) price.textContent = formatMoney(option.dataset.price);
      setMainImage(root, option?.dataset.imageSrc);
      if (button && option) {
        const available = option.dataset.available === 'true';
        button.disabled = !available;
        button.textContent = available ? button.dataset.availableText : button.dataset.soldOutText;
      }
    }

    const qrToggle = event.target.closest('[data-qr-toggle]');
    if (qrToggle) {
      const field = document.getElementById(qrToggle.getAttribute('aria-controls'));
      if (field) {
        field.hidden = !qrToggle.checked;
        const input = field.querySelector('input');
        if (input) input.disabled = !qrToggle.checked;
      }
    }
  });

  document.querySelectorAll('[data-amtap-configurator]').forEach(setupConfigurator);
  document.querySelectorAll('[data-cart-form]').forEach(setupCart);

  document.querySelectorAll('[data-use-tabs]').forEach((tabs) => {
    const buttons = tabs.querySelectorAll('[role="tab"]');
    const panels = tabs.querySelectorAll('[role="tabpanel"]');
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        buttons.forEach((item) => item.setAttribute('aria-selected', 'false'));
        panels.forEach((panel) => { panel.hidden = true; });
        button.setAttribute('aria-selected', 'true');
        const panel = document.getElementById(button.getAttribute('aria-controls'));
        if (panel) panel.hidden = false;
      });
    });
  });
})();
